import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { availableLanguagesForUser, userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useMerchantFeatures from '@/common/hooks/useMerchantFeatures/useMerchantFeatures'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import PortalsInUseList from '@/configuration/components/PortalsInUseList/PortalsInUseList'
import SettingsSection from '@/configuration/components/SettingsSection/SettingsSection'
import { settingsTypes } from '@/configuration/constants'
import { sectionTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getConfigurationMode, getInfoWithUpdatedPreferences } from '@/configuration/pages/ConfigurationPage/helpers'
import useSettingsType from '@/configuration/pages/ConfigurationPage/hooks/useSettingsType/useSettingsType'

import BundlesConfiguration from './BundlesConfiguration/BundlesConfiguration'
import DiscountsConfiguration from './DiscountsConfiguration/DiscountsConfiguration'
import ExchangesConfiguration from './ExchangesConfiguration/ExchangesConfiguration'
import GiftReturnsConfiguration from './GiftReturnsConfiguration/GiftReturnsConfiguration'
import useReturnPolicyConfiguration from './hooks/useReturnPolicyConfiguration/useReturnPolicyConfiguration'
import RestockProductsConfiguration from './RestockProductsConfiguration/RestockProductsConfiguration'
import ReturnCancelDeadLine from './ReturnCancelDeadLine/ReturnCancelDeadLine'
import ReturnPolicyConfigurationSkeleton from './ReturnPolicyConfigurationSkeleton/ReturnPolicyConfigurationSkeleton'
import ReturnsConfiguration from './ReturnsConfiguration/ReturnsConfiguration'
import { getPayload, isFormValid } from './helpers'
import * as Styles from './styles'

const ReturnPolicyConfiguration = ({ isUnifiedDashboard }) => {
  const { t } = useTranslation()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)

  const {
    settingsType,
    setSettingsType,
    hasChanges: settingsTypeHasChanges,
    revertSettingsType,
    onSaveSettingsType,
    loadingStoreSettingsType,
  } = useSettingsType({
    returnPortalId: info?.selectedReturnPortal?.id,
    section: sectionTypes.returnPolicy,
    isUnifiedDashboard,
    setToastInfo,
  })

  const configurationMode = getConfigurationMode({ isUnifiedDashboard, settingsType })

  const {
    configurationPreferences,
    loadingConfigurationPreferences,
    refetchConfigurationPreferences,
    allowReturns,
    setAllowReturns,
    maxDurationForRefund,
    setMaxDurationForRefund,
    shippingIncluded,
    setShippingIncluded,
    allowExchanges,
    setAllowExchanges,
    maxDurationForExchanges,
    setMaxDurationForExchanges,
    allowedExchangeMethods,
    setAllowedExchangeMethods,
    exchangeOrdersAutomaticCreation,
    setExchangeOrdersAutomaticCreation,
    allowInstantExchanges,
    setAllowInstantExchanges,
    instantExchangeOrdersAutomaticCreation,
    setInstantExchangeOrdersAutomaticCreation,
    allowEvenExchangesCatalogPrice,
    setAllowEvenExchangesCatalogPrice,
    issueExchangeAtStatus,
    setIssueExchangeAtStatus,
    exchangeOrdersSendProviderNotification,
    setExchangeOrdersSendProviderNotification,
    instantExchangeOrdersSendProviderNotification,
    setInstantExchangeOrdersSendProviderNotification,
    gettingProductCategories,
    gettingProductCollections,
    fetchingProductTypes,
    savePreferences,
    savingPreferences,
    exchangeErrorResolution,
    setExchangeErrorResolution,
    hasChanges,
    revertChanges,
    returnCancelDeadlineDays,
    setReturnCancelDeadlineDays,
    availableIssueExchangeStatus,
    manageRestock,
    setManageRestock,
    refundReferenceDate,
    setRefundReferenceDate,
    exchangeReferenceDate,
    setExchangeReferenceDate,
    allowReturnCancelDeadline,
    setAllowReturnCancelDeadline,
    entityId,
    bundleReturnPolicy,
    setBundleReturnPolicy,
    bundleAllowedRequests,
    setBundleAllowedRequests,
    buyXGetYReturnPolicy,
    setBuyXGetYReturnPolicy,
    brokenDiscountHandling,
    setBrokenDiscountHandling,
    allowsBrokenDiscountHandling,
    brokenDiscountBonusEnabled,
    setBrokenDiscountBonusEnabled,
    brokenDiscountCustomMessageEnabled,
    setBrokenDiscountCustomMessageEnabled,
    brokenDiscountCustomMessage,
    setBrokenDiscountCustomMessage,
    exchangePaymentExpiration,
    setExchangePaymentExpiration,
    instantExchangePaymentExpiration,
    setInstantExchangePaymentExpiration,
    allowsGiftReturns,
    setAllowsGiftReturns,
    skipIdentifierValidation,
    setSkipIdentifierValidation,
    giftCardAllowed,
  } = useReturnPolicyConfiguration({
    storeId: info?.selectedStore?.id,
    merchantId: info?.id,
    returnPortalId: info?.selectedReturnPortal?.id,
    configurationMode,
  })
  const { hasPermission } = useUserPermissions()
  const { returnPortalFeatures } = useReturnPortalFeatures({
    returnPortalId: info?.selectedReturnPortal?.id,
    doInitialRequest: !isUnifiedDashboard,
  })
  const { merchantFeatures } = useMerchantFeatures({
    merchantId: info?.id,
    doInitialRequest: isUnifiedDashboard === true,
  })

  const provider = info?.selectedStore?.provider

  const supportedLanguages = info?.selectedStore?.supported_languages || ['en']
  const brokenDiscountCustomMessageLanguages = availableLanguagesForUser.filter(language =>
    supportedLanguages.includes(language)
  )

  const externalConnectedGateway = info?.billing_data?.external_connected_gateway

  const allowedPreferences = Object.keys(configurationPreferences || {})

  const saveSettingsAndUpdateContext = async preferencesPayload => {
    if (!isUnifiedDashboard) {
      if (settingsTypeHasChanges) {
        await onSaveSettingsType()
      }
      updateContext(info => getInfoWithUpdatedPreferences(info, preferencesPayload))
    }
    await refetchConfigurationPreferences(entityId)
  }

  const handleSavePreferences = async payload => {
    if (hasChanges) {
      await savePreferences({ ...entityId, payload: { ...configurationPreferences, ...payload } })
    }

    await saveSettingsAndUpdateContext(payload)
  }

  const onSavePreferences = async () => {
    const payload = getPayload({
      allowReturns,
      maxDurationForRefund,
      shippingIncluded,
      allowExchanges,
      maxDurationForExchanges,
      allowedExchangeMethods,
      exchangeOrdersAutomaticCreation,
      allowInstantExchanges,
      instantExchangeOrdersAutomaticCreation,
      allowEvenExchangesCatalogPrice,
      issueExchangeAtStatus,
      exchangeOrdersSendProviderNotification,
      instantExchangeOrdersSendProviderNotification,
      exchangeErrorResolution,
      returnCancelDeadlineDays,
      manageRestock,
      refundReferenceDate,
      exchangeReferenceDate,
      bundleReturnPolicy,
      bundleAllowedRequests,
      buyXGetYReturnPolicy,
      brokenDiscountHandling,
      brokenDiscountBonusEnabled,
      brokenDiscountCustomMessageEnabled,
      brokenDiscountCustomMessage,
      exchangePaymentExpiration,
      instantExchangePaymentExpiration,
      allowsGiftReturns,
      skipIdentifierValidation,
    })

    try {
      await handleSavePreferences(payload)
      setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleChangeSettingsType = type => {
    setSettingsType(type)
  }

  const handleResetChanges = () => {
    revertSettingsType()
    revertChanges()
  }

  const canEditPolicy = hasPermission(userPermissions.editPolicy)
  const isFieldDisabled = !canEditPolicy || (!isUnifiedDashboard && settingsType === settingsTypes.global)

  if (
    loadingConfigurationPreferences ||
    gettingProductCategories ||
    gettingProductCollections ||
    fetchingProductTypes ||
    savingPreferences ||
    loadingStoreSettingsType
  ) {
    return <ReturnPolicyConfigurationSkeleton />
  }

  return (
    <Styles.MainContainer>
      {(hasChanges || settingsTypeHasChanges) && <UnsavedChangesAlert hasPermission={canEditPolicy} />}
      {hasPermission(userPermissions.viewPolicy) && (
        <>
          {isUnifiedDashboard && (
            <Styles.PortalsInUseContainer>
              <PortalsInUseList portals={info.returnPortals} portalsInUse={configurationPreferences?.enabled_portals} />
            </Styles.PortalsInUseContainer>
          )}
          {!isUnifiedDashboard && info.allows_unified_dashboard && (
            <SettingsSection type={settingsType} onChange={handleChangeSettingsType} disabled={!canEditPolicy} />
          )}
          <Text type="bodyMRegular">{t('Configura los tipos de operaciones permitidas y su comportamiento')}:</Text>

          <Text type="titleM">{t('Transacciones permitidas')}</Text>
          <ReturnsConfiguration
            allowReturns={allowReturns}
            onChangeAllowReturns={setAllowReturns}
            allowedPreferences={allowedPreferences}
            maxDurationForRefund={maxDurationForRefund}
            shippingIncluded={shippingIncluded}
            onChangeShippingIncluded={setShippingIncluded}
            onChangeMaxDurationForRefund={setMaxDurationForRefund}
            refundReferenceDate={refundReferenceDate}
            onChangeRefundReferenceDate={setRefundReferenceDate}
            disabled={isFieldDisabled}
          />
          <ExchangesConfiguration
            allowedPreferences={allowedPreferences}
            configurationPreferences={configurationPreferences}
            provider={provider}
            allowExchanges={allowExchanges}
            setAllowExchanges={setAllowExchanges}
            maxDurationForExchanges={maxDurationForExchanges}
            setMaxDurationForExchanges={setMaxDurationForExchanges}
            allowedExchangeMethods={allowedExchangeMethods}
            setAllowedExchangeMethods={setAllowedExchangeMethods}
            exchangeOrdersAutomaticCreation={exchangeOrdersAutomaticCreation}
            setExchangeOrdersAutomaticCreation={setExchangeOrdersAutomaticCreation}
            issueExchangeAtStatus={issueExchangeAtStatus}
            setIssueExchangeAtStatus={setIssueExchangeAtStatus}
            exchangeErrorResolution={exchangeErrorResolution}
            setExchangeErrorResolution={setExchangeErrorResolution}
            exchangeOrdersSendProviderNotification={exchangeOrdersSendProviderNotification}
            setExchangeOrdersSendProviderNotification={setExchangeOrdersSendProviderNotification}
            allowEvenExchangesCatalogPrice={allowEvenExchangesCatalogPrice}
            setAllowEvenExchangesCatalogPrice={setAllowEvenExchangesCatalogPrice}
            allowInstantExchanges={allowInstantExchanges}
            setAllowInstantExchanges={setAllowInstantExchanges}
            instantExchangeOrdersAutomaticCreation={instantExchangeOrdersAutomaticCreation}
            setInstantExchangeOrdersAutomaticCreation={setInstantExchangeOrdersAutomaticCreation}
            instantExchangeOrdersSendProviderNotification={instantExchangeOrdersSendProviderNotification}
            setInstantExchangeOrdersSendProviderNotification={setInstantExchangeOrdersSendProviderNotification}
            availableIssueExchangeStatus={availableIssueExchangeStatus}
            allowsUnifiedFlow={info?.selectedStore?.preferences?.allows_unified_flow}
            subscriptionPlan={info.subscription_plan}
            exchangeReferenceDate={exchangeReferenceDate}
            onChangeExchangeReferenceDate={setExchangeReferenceDate}
            externalConnectedGateway={externalConnectedGateway}
            isUnifiedDashboard={isUnifiedDashboard}
            disabled={isFieldDisabled}
            allowsInstantTransactions={
              isUnifiedDashboard
                ? merchantFeatures?.allows_instant_transactions
                : returnPortalFeatures?.allows_instant_transactions
            }
            exchangePaymentExpiration={exchangePaymentExpiration}
            setExchangePaymentExpiration={setExchangePaymentExpiration}
            instantExchangePaymentExpiration={instantExchangePaymentExpiration}
            setInstantExchangePaymentExpiration={setInstantExchangePaymentExpiration}
          />
        </>
      )}
      <GiftReturnsConfiguration
        allowsGiftReturns={allowsGiftReturns}
        onChangeAllowsGiftReturns={setAllowsGiftReturns}
        disabled={isFieldDisabled}
        allowsUnifiedFlow={info?.selectedStore?.preferences?.allows_unified_flow}
        skipIdentifierValidation={skipIdentifierValidation}
        onChangeSkipIdentifierValidation={setSkipIdentifierValidation}
        giftCardAllowed={giftCardAllowed}
      />
      <DiscountsConfiguration
        brokenDiscountHandling={brokenDiscountHandling}
        setBrokenDiscountHandling={setBrokenDiscountHandling}
        allowsBrokenDiscountHandling={allowsBrokenDiscountHandling}
        isUnifiedDashboard={isUnifiedDashboard}
        buyXGetYReturnPolicy={buyXGetYReturnPolicy}
        setBuyXGetYReturnPolicy={setBuyXGetYReturnPolicy}
        brokenDiscountBonusEnabled={brokenDiscountBonusEnabled}
        setBrokenDiscountBonusEnabled={setBrokenDiscountBonusEnabled}
        brokenDiscountCustomMessageEnabled={brokenDiscountCustomMessageEnabled}
        setBrokenDiscountCustomMessageEnabled={setBrokenDiscountCustomMessageEnabled}
        brokenDiscountCustomMessage={brokenDiscountCustomMessage}
        setBrokenDiscountCustomMessage={setBrokenDiscountCustomMessage}
        disabled={isFieldDisabled}
      />
      <BundlesConfiguration
        bundleReturnPolicy={bundleReturnPolicy}
        setBundleReturnPolicy={setBundleReturnPolicy}
        bundleAllowedRequests={bundleAllowedRequests}
        setBundleAllowedRequests={setBundleAllowedRequests}
        allowReturns={allowReturns}
        allowExchanges={allowExchanges}
        disabled={isFieldDisabled}
      />
      <RestockProductsConfiguration
        setManageRestock={setManageRestock}
        manageRestock={manageRestock}
        allowedPreferences={allowedPreferences}
        disabled={isFieldDisabled}
      />
      <ReturnCancelDeadLine
        allowReturnCancelDeadline={allowReturnCancelDeadline}
        onChangeAllowReturnCancelDeadline={setAllowReturnCancelDeadline}
        returnCancelDeadLine={returnCancelDeadlineDays}
        setReturnCancelDeadLine={setReturnCancelDeadlineDays}
        allowedPreferences={allowedPreferences}
        disabled={isFieldDisabled}
      />
      <SaveChangesButtons
        hasChanges={canEditPolicy && (hasChanges || settingsTypeHasChanges)}
        onSave={onSavePreferences}
        onRevertChanges={handleResetChanges}
        loading={savingPreferences}
        disabled={
          !isFormValid({
            allowReturns,
            maxDurationForRefund,
            allowExchanges,
            maxDurationForExchanges,
            allowReturnCancelDeadline,
            returnCancelDeadlineDays,
            exchangePaymentExpiration,
            instantExchangePaymentExpiration,
            brokenDiscountHandling,
            brokenDiscountCustomMessageEnabled,
            brokenDiscountCustomMessage,
            brokenDiscountCustomMessageLanguages,
          }) || savingPreferences
        }
      />
    </Styles.MainContainer>
  )
}

export default ReturnPolicyConfiguration
